"use client";

import Link from "next/link";
import { useMemo } from "react";
import {
  Activity,
  CheckCircle2,
  Gauge,
  Route,
} from "lucide-react";
import { RecentSessions } from "@/app/_components/companion/RecentSessions";
import { ScoreChip } from "@/app/_components/verdict/ScoreChip";
import { VerdictPill } from "@/app/_components/verdict/VerdictPill";
import { CompanionAvailabilityForm } from "./CompanionAvailabilityForm";
import { CompanionFeedbackPanel } from "./CompanionFeedbackPanel";
import { CompanionBaseSheet } from "./CompanionBaseSheet";
import { SquadManagementCard } from "./SquadManagementCard";
import { NativeWidgetSnapshotSync } from "@/app/my-ski/NativeWidgetSnapshotSync";
import { SafetyDisclaimer } from "@/app/_components/SafetyDisclaimer";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import type {
  CompanionHomeData,
  CompanionHomeModel,
  CompanionSavedPlace,
} from "@/lib/companion/home-model";
import {
  buildCompanionHomeModel,
  resolveNativeWidgetHomeResortSlug,
} from "@/lib/companion/home-model";
import {
  buildCompanionAlertsView,
  buildCompanionZoneAState,
  companionDayBoardCopy,
  companionPrivateModulesEnabled,
  companionHomeStateCopy,
  companionSetupProgress,
} from "./companion-home-state";
import { CompanionPlanWorkspace, SetupTile } from "./_components/CompanionPlanWorkspace";
import { CrewDirectoryCard } from "./_components/CompanionPeopleWorkspace";
import { RecordCard } from "./_components/CompanionRecordWorkspace";
import { AlertsSection } from "./_components/CompanionSettingsWorkspace";
import { CompanionDownDayBlock } from "./_components/CompanionDownDayBlock";
import {
  CompanionSetupContent,
  useCompanionSignupIntentPrefill,
} from "./_components/CompanionSetupPanel";

export type CompanionHomeProps = {
  data: CompanionHomeData;
  model: CompanionHomeModel;
  renderedAt: string;
  fixtureLabel?: string | null;
  includeNamedSupProfiles?: boolean;
};

export function CompanionHome({
  data,
  model,
  renderedAt,
  fixtureLabel = null,
  includeNamedSupProfiles = false,
}: CompanionHomeProps) {
  const viewedBaseId = useShallowSearchParam("base");
  const resolvedModel = useMemo(() => {
    if (!viewedBaseId) return model;
    return buildCompanionHomeModel(data, {
      now: new Date(renderedAt),
      viewedBaseId,
    });
  }, [data, model, renderedAt, viewedBaseId]);

  const copy = companionHomeStateCopy(resolvedModel);
  const privateModulesEnabled = companionPrivateModulesEnabled(resolvedModel);
  const bestPlace = resolvedModel.visiblePlace;
  const groundTruthPromptPlace = resolvedModel.groundTruthPromptPlace;
  const feedbackPlace = groundTruthPromptPlace ?? bestPlace;
  const record = resolvedModel.record;
  const dayCopy = companionDayBoardCopy(resolvedModel);
  const setupProgress = companionSetupProgress(resolvedModel);
  const intentPrefill = useCompanionSignupIntentPrefill(
    renderedAt,
    privateModulesEnabled && !setupProgress.isComplete,
    includeNamedSupProfiles,
  );
  const alertsView = buildCompanionAlertsView(data, resolvedModel);
  const zoneAState = useMemo(
    () =>
      buildCompanionZoneAState(data, resolvedModel, {
        now: new Date(renderedAt),
      }),
    [data, renderedAt, resolvedModel],
  );
  const nativeWidgetHomeResortSlug = resolveNativeWidgetHomeResortSlug(resolvedModel);

  return (
    <div className="min-h-screen bg-warm-bg text-charcoal">
      <NativeWidgetSnapshotSync homeResortSlug={nativeWidgetHomeResortSlug} owner={data.user?.id ?? "device"} disabled={!!fixtureLabel} />
      <section className="bg-navy px-5 pb-8 pt-9 text-white md:px-10 md:pb-10 md:pt-12">
        <div className="mx-auto grid max-w-[1160px] gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(420px,1.05fr)] lg:items-end">
          <div className="max-w-[660px]">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[2px] text-gold-light">
              {copy.kicker}
            </p>
            <h1 className="font-serif text-[46px] leading-[0.98] tracking-normal text-white md:text-[64px]">
              {copy.title}
            </h1>
            <p className="mt-4 max-w-[56ch] text-[15px] leading-relaxed text-white/78 md:text-[16px]">
              {copy.body}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full border border-white/14 bg-white/8 px-3 py-1.5 text-[12px] font-semibold text-white/78">
                {dayCopy.placeLabel}
              </span>
              <span className="rounded-full border border-gold/24 bg-gold/10 px-3 py-1.5 text-[12px] font-semibold text-gold-light">
                {dayCopy.bestSignalLabel}
              </span>
              <span className="rounded-full border border-white/14 bg-white/8 px-3 py-1.5 text-[12px] font-semibold text-white/78">
                Compare before you move
              </span>
            </div>
            <CompanionBaseSheet
              key={intentPrefill?.region?.slug ?? "base-sheet"}
              model={resolvedModel}
              intentPrefill={intentPrefill}
            />
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                href={copy.primaryCta.href}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm bg-gold px-5 py-3 text-[14px] font-semibold text-navy no-underline transition-colors hover:bg-gold-light"
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {copy.primaryCta.label}
              </Link>
              <Link
                href={privateModulesEnabled ? "/companion/track" : "/conditions"}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-white/18 px-5 py-3 text-[14px] font-semibold text-white no-underline transition-colors hover:border-gold/60 hover:bg-white/8"
              >
                {privateModulesEnabled ? (
                  <Activity className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Gauge className="h-4 w-4" aria-hidden="true" />
                )}
                {privateModulesEnabled ? "Track now" : "View conditions"}
              </Link>
            </div>
          </div>

          <TodayPanel
            model={resolvedModel}
            place={bestPlace}
            dayCopy={dayCopy}
            zoneAState={zoneAState}
            availabilityWindows={data.availabilityManagementWindows}
            renderedAt={renderedAt}
          />
        </div>
      </section>

      {fixtureLabel ? (
        <div className="mx-auto max-w-[1160px] px-5 pt-4 md:px-10">
          <p className="rounded-sm border border-gold/30 bg-gold/10 px-3 py-2 text-[12px] font-semibold text-navy">
            {fixtureLabel}
          </p>
        </div>
      ) : null}

      {privateModulesEnabled ? (
        <section className="mx-auto grid max-w-[1160px] gap-5 px-5 py-6 md:px-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <CompanionSetupContent
              model={resolvedModel}
              intentPrefill={intentPrefill}
              includeBaseSheet={false}
            />
            <CompanionPlanWorkspace
              data={data}
              model={resolvedModel}
              renderedAt={renderedAt}
            />
          </div>

          <aside className="space-y-5">
            <CompanionFeedbackPanel
              place={feedbackPlace}
              isLoggedIn={Boolean(data.user)}
              heading={groundTruthPromptPlace ? "How was it really?" : "Ground truth"}
              kicker={groundTruthPromptPlace ? "Ground truth" : "Forecast check"}
            />
            <AlertsSection view={alertsView} isLoggedIn={Boolean(data.user)} />
            {resolvedModel.crew.length > 0 ? (
              <CrewDirectoryCard crews={resolvedModel.crew} />
            ) : null}
            <RecordCard
              model={resolvedModel}
              record={record}
            />
          </aside>
        </section>
      ) : (
        <section className="mx-auto max-w-[1160px] px-5 py-6 md:px-10">
          <PublicCompanionTeaser />
        </section>
      )}

      {privateModulesEnabled ? (
        <section className="mx-auto max-w-[1160px] px-5 pb-10 md:px-10">
          <RecentSessions />
        </section>
      ) : null}

      {privateModulesEnabled ? (
        <section className="mx-auto max-w-[1160px] px-5 pb-6 md:px-10">
          <SquadManagementCard squads={resolvedModel.squads} />
        </section>
      ) : null}

      {privateModulesEnabled ? (
        <section className="mx-auto max-w-[1160px] px-5 pb-10 md:px-10">
          <WorkBridgeCard />
        </section>
      ) : null}
    </div>
  );
}

function TodayPanel({
  model,
  place,
  dayCopy,
  zoneAState,
  availabilityWindows,
  renderedAt,
}: {
  model: CompanionHomeModel;
  place: CompanionSavedPlace | null;
  dayCopy: ReturnType<typeof companionDayBoardCopy>;
  zoneAState: ReturnType<typeof buildCompanionZoneAState>;
  availabilityWindows: CompanionHomeData["availabilityManagementWindows"];
  renderedAt: string;
}) {
  const primary = model.today[0] ?? null;

  return (
    <section
      aria-labelledby="today-heading"
      className="rounded-md border border-white/12 bg-white/[0.06] p-4 shadow-[0_18px_70px_rgba(0,0,0,0.24)] backdrop-blur md:p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.7px] text-gold-light">
            Today
          </p>
          <h2 id="today-heading" className="font-serif text-[28px] leading-tight text-white md:text-[32px]">
            {zoneAState.greeting}
          </h2>
          <p className="mt-1 text-[12.5px] leading-relaxed text-white/58">
            {zoneAState.dateLine}
          </p>
        </div>
        <span className="rounded-full border border-white/14 px-3 py-1 text-[11px] font-semibold uppercase tracking-[1.3px] text-white/70">
          {dayCopy.placeLabel}
        </span>
      </div>

      {zoneAState.callTakeover ? (
        <div className="mt-4 rounded-sm border border-gold/55 bg-gold/10 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[1.5px] text-gold-light">
            The Call
          </p>
          <p className="mt-2 font-serif text-[24px] leading-tight text-white">
            {zoneAState.callTakeover.title}
          </p>
          {zoneAState.callTakeover.windowCopy ? (
            <p className="mt-2 text-[13px] leading-relaxed text-white/78">
              {zoneAState.callTakeover.windowCopy}
            </p>
          ) : null}
          <Link
            href={zoneAState.callTakeover.href}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-sm border border-gold/45 px-4 py-2 text-[13px] font-semibold text-gold-light no-underline hover:bg-gold/10"
          >
            Open the Call
          </Link>
        </div>
      ) : null}

      <div className="mt-4 rounded-sm border border-white/12 bg-white/[0.07] p-4">
        <div className="flex flex-wrap items-center gap-2">
          <VerdictPill tone={zoneAState.verdict.actionTone} />
          <span className="rounded-full border border-white/14 px-3 py-1 text-[11px] font-semibold uppercase tracking-[1.3px] text-white/66">
            {zoneAState.verdict.confidenceLabel}
          </span>
          <span className="rounded-full border border-white/14 px-3 py-1 text-[11px] font-semibold uppercase tracking-[1.3px] text-white/66">
            {zoneAState.verdict.freshnessLabel}
          </span>
          {model.status === "ready" ? (
            <ScoreChip score={zoneAState.verdict.score} />
          ) : null}
          {zoneAState.verdict.confirmedLabel ? (
            <span className="rounded-full border border-emerald-400/35 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold text-emerald-100">
              {zoneAState.verdict.confirmedLabel}
            </span>
          ) : null}
        </div>
        <p className="mt-3 font-serif text-[27px] leading-tight text-white md:text-[30px]">
          {zoneAState.verdict.verdict}
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-white/68">
          {zoneAState.verdict.reason}
        </p>
        {zoneAState.verdict.windowLine ? (
          <p className="mt-3 text-[12.5px] font-semibold text-gold-light">
            {zoneAState.verdict.windowLine}
          </p>
        ) : null}
        {zoneAState.verdict.fitLine ? (
          <p className="mt-2 text-[12.5px] text-white/68">
            {zoneAState.verdict.fitLine}
          </p>
        ) : null}
        {zoneAState.verdict.nextUsefulLine ? (
          <p className="mt-2 text-[12.5px] text-white/58">
            {zoneAState.verdict.nextUsefulLine}
          </p>
        ) : null}
        <p className="mt-3 rounded-sm border border-white/12 bg-white/[0.05] px-3 py-2 text-[12.5px] leading-relaxed text-white/78">
          {zoneAState.verdict.safetyLine}
        </p>
        <div className="mt-3">
          <SafetyDisclaimer tone="dark" />
        </div>
      </div>

      <CompanionAvailabilityForm
        windows={availabilityWindows}
        fitContext={zoneAState.availabilityFitContext}
        renderedAt={renderedAt}
      />

      {zoneAState.downDay ? (
        <CompanionDownDayBlock downDay={zoneAState.downDay} />
      ) : null}

      {zoneAState.crossBaseLine ? (
        <p className="mt-3 text-[12.5px] leading-relaxed text-white/58">
          Elsewhere:{" "}
          <Link
            href={zoneAState.crossBaseLine.href}
            className="font-semibold text-white/82 no-underline hover:text-gold-light"
          >
            {zoneAState.crossBaseLine.name}
          </Link>{" "}
          ({zoneAState.crossBaseLine.baseName}) is at{" "}
          {scoreOutOf100(zoneAState.crossBaseLine.score)}/100.
        </p>
      ) : null}

      {zoneAState.nearbyBetterLine ? (
        <p className="mt-3 text-[12.5px] leading-relaxed text-white/58">
          Better within reach:{" "}
          <Link
            href={zoneAState.nearbyBetterLine.href}
            className="font-semibold text-white/82 no-underline hover:text-gold-light"
          >
            {zoneAState.nearbyBetterLine.name}
          </Link>, about {zoneAState.nearbyBetterLine.driveMinutes} min drive,{" "}
          {zoneAState.nearbyBetterLine.sportLabel}{" "}
          {scoreOutOf100(zoneAState.nearbyBetterLine.score)}/100.
        </p>
      ) : null}

      {place && zoneAState.bestPlace ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-[120px_minmax(0,1fr)]">
          <div className="rounded-sm bg-white px-4 py-4 text-navy">
            <p className="text-[11px] font-semibold uppercase tracking-[1.5px] text-text-muted">
              Best place
            </p>
            <div className="mt-3">
              <ScoreChip score={zoneAState.bestPlace.score} />
            </div>
            <p className="mt-3 text-[11px] font-semibold text-text-muted">
              {zoneAState.bestPlace.freshnessLabel}
            </p>
          </div>
          <div className="rounded-sm border border-white/12 bg-white/[0.07] p-4">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-serif text-[24px] leading-tight text-white">
                {zoneAState.bestPlace.name}
              </p>
              {zoneAState.bestPlace.region ? (
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/72">
                  {zoneAState.bestPlace.region}
                </span>
              ) : null}
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-white/70">
              {zoneAState.bestPlace.verdict}
            </p>
            {zoneAState.bestPlace.windowLine ? (
              <p className="mt-3 text-[12.5px] font-semibold text-gold-light">
                {zoneAState.bestPlace.windowLine}
              </p>
            ) : null}
            <p className="mt-3 rounded-sm border border-sky-200/30 bg-sky-50/10 px-3 py-2 text-[12px] leading-relaxed text-sky-50">
              <span className="font-semibold">{zoneAState.bestPlace.disclosure.label}.</span>{" "}
              {zoneAState.bestPlace.disclosure.body}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                href={zoneAState.bestPlace.href}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm bg-white px-4 py-2 text-[13px] font-semibold text-navy no-underline hover:bg-cream"
              >
                <Gauge className="h-4 w-4" aria-hidden="true" />
                Open conditions
              </Link>
              {zoneAState.bestPlace.plannerHref ? (
                <Link
                  href={zoneAState.bestPlace.plannerHref}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-white/18 px-4 py-2 text-[13px] font-semibold text-white no-underline hover:border-gold/60"
                >
                  <Route className="h-4 w-4" aria-hidden="true" />
                  Plan my day
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {model.status === "anonymous" ? (
            <>
              <SetupTile
                href="/signup?intent=companion&next=/companion"
                title="Create your Companion"
                body="Save places, opt into Briefings and keep your season record in one private home."
              />
              <SetupTile
                href="/conditions"
                title="View live conditions"
                body="Check mountains and coast before deciding where to go."
              />
            </>
          ) : (
            <>
              <SetupTile
                href="/conditions"
                title="Save a place"
                body="Choose a mountain or coast place so Companion can watch it."
              />
              <SetupTile
                href="/companion/track"
                title="Track a day"
                body="Record the next session and start building private proof."
              />
            </>
          )}
        </div>
      )}

      {primary ? (
        <p className="mt-4 text-[12px] text-white/58">
          Also worth checking: <span className="font-semibold text-white/82">{primary.title}</span>
        </p>
      ) : null}
    </section>
  );
}

function PublicCompanionTeaser() {
  return (
    <section aria-labelledby="public-companion-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
            Preview
          </p>
          <h2 id="public-companion-heading" className="font-serif text-[28px] leading-tight text-navy">
            One board for the day you might actually take
          </h2>
          <p className="mt-2 text-[13.5px] leading-relaxed text-text-muted">
            Companion starts with live conditions, then adds saved places, explicit Briefings and private tracking after sign-up.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              href="/signup?intent=companion&next=/companion"
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-4 py-2 text-[13px] font-semibold text-white no-underline hover:bg-navy/90"
            >
              Sign up
            </Link>
            <Link
              href="/conditions"
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-4 py-2 text-[13px] font-semibold text-navy no-underline hover:border-gold/50"
            >
              Conditions
            </Link>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <SetupTile
            href="/conditions"
            title="Watch a place"
            body="Pick a mountain or coast spot and see the latest signal."
          />
          <SetupTile
            href="/signup?intent=companion&next=/companion"
            title="Opt into Briefings"
            body="Morning context stays explicit. Saving a place does not turn on email or push."
          />
          <SetupTile
            href="/signup?intent=companion&next=/companion"
            title="Build your record"
            body="Tracking creates distance, vertical, speed and Trip Story proof."
          />
        </div>
      </div>
    </section>
  );
}

function WorkBridgeCard() {
  return (
    <section
      aria-label="Season work"
      className="rounded-md border border-border-light bg-white px-4 py-3 md:px-5"
    >
      <p className="flex min-h-[44px] flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-relaxed text-text-muted">
        <span className="font-semibold text-navy">Looking for next season?</span>
        <Link
          href="/jobs"
          className="inline-flex min-h-[44px] items-center font-semibold text-navy underline decoration-gold/50 underline-offset-4 hover:text-gold"
        >
          Jobs
        </Link>
        <span aria-hidden="true" className="text-border">
          |
        </span>
        <Link
          href="/profile?setup=work"
          className="inline-flex min-h-[44px] items-center font-semibold text-navy underline decoration-gold/50 underline-offset-4 hover:text-gold"
        >
          Set up your work profile
        </Link>
      </p>
    </section>
  );
}
